/**
 * singleton, call getInstance() to interact with this class
 * provides a simple API to handle CSS variables (custom-properties) values from any external source
 */

enum AVAILABLE_THEMES {
  AMETHYST = 'amethyst',
  AQUA_DREAMS = 'aqua-dreams',
  ARTECHOUSE = 'artechouse',
  BUBBLE_GUM = 'bubble-gum',
  COCOA = 'cocoa',
  COUNTY_LINE_ORCHARDS = 'county-line-orchards',
  CRACKED_PEPPER = 'cracked-pepper',
  DESERT_SAND = 'desert-sand',
  DIAMOND_BLUE = 'diamond-blue',
  DRAGONSCALES = 'dragonscales',
  FORESTS_EDGE = 'forests-edge',
  GREEN_TEA = 'green-tea',
  ICE_CREAM = 'ice-cream',
  IMMERSIVE_GAMEBOX = 'immersive-gamebox',
  JELLYCAT = 'jellycat',
  KING_PLEASURE = 'king-pleasure',
  LAVENDER_SPRIG = 'lavender-sprig',
  MIDDAY_SUN = 'midday-sun',
  MIDNIGHT_BLUE = 'midnight-blue',
  MORNING_SUN = 'morning-sun',
  NEUTRALITY = 'neutrality',
  PEACHY_PEACH = 'peachy-peach',
  PLUM_WINO = 'plum-wino',
  POWDER_BLUE = 'powder-blue',
  PURPLE_RAIN = 'purple-rain',
  RED_VELVET = 'red-velvet',
  SKY_BLUE = 'sky-blue',
  SMOKE_GREY = 'smoke-grey',
  SPACE_BLUE = 'space-blue',
  STARLESS_SKY = 'starless-sky',
  STRATUS_GREY = 'stratus-grey',
  TURQUOISE_BREEZE = 'turquoise-breeze',
  BRIGHT_PINK = 'bright-pink',
  RED = 'red',
  BROWN = 'brown',
  LIME = 'lime',
  GREEN = 'green',
  AQUA = 'aqua',
  COBALT = 'cobalt',
  INDIGO = 'indigo',
  BLACK = 'black',
  PINK = 'pink',
  DEEP_RED = 'deep-red',
  ORANGE = 'orange',
  DEEP_BROWN = 'deep-brown',
  WOOD_BROWN = 'wood-brown',
  OLIVE_GREEM = 'olive-green',
  DEEP_GREEN = 'deep-green',
  TEAL = 'teal',
  TURQUOISE = 'turquoise',
  BLUE = 'blue',
  SHAPPHIRE = 'sapphire',
  DEEP_BLUE = 'deep-blue',
  DEEP_PURPLE = 'deep-purple',
  PURPLE = 'purple',
  PLUM = 'plum',
  CHARCOAL = 'charcoal',
}

export class IliadThemeHandler {
  private static instance: IliadThemeHandler

  /**
   * contains all base variables for Iliad
   * any replaceable variable must be defined here
   * @private
   */
  private themeVariablesMap: { [iliadVariableName: string]: string } = {
    '--il-color-accent-50': '--il-theme-{THEME_NAME}-color-accent-50',
    '--il-color-accent-100': '--il-theme-{THEME_NAME}-color-accent-100',
    '--il-color-accent-200': '--il-theme-{THEME_NAME}-color-accent-200',
    '--il-color-accent-300': '--il-theme-{THEME_NAME}-color-accent-300',
    '--il-color-accent-400': '--il-theme-{THEME_NAME}-color-accent-400',
    '--il-color-warning': '--il-theme-{THEME_NAME}-color-warning'
  }

  public static getInstance (): IliadThemeHandler {
    if (!this.instance) {
      this.instance = new IliadThemeHandler()
    }

    return this.instance
  }

  /**
   * sets CSS variables via theme name, using this.themeVariablesMap
   * TODO check if this works correctly
   * @param themeName
   */
  public setTheme (themeName: AVAILABLE_THEMES): void {
    console.assert(!!themeName, 'themeName must be defined')
    let cleanedTheme = (themeName || AVAILABLE_THEMES.SKY_BLUE).replace(/_/g, '-') as AVAILABLE_THEMES

    if (!Object.values(AVAILABLE_THEMES).includes(cleanedTheme)) {
      cleanedTheme = AVAILABLE_THEMES.SKY_BLUE
    }

    Object.keys(this.themeVariablesMap).forEach(iliadVariableName => {
      const cssVariableName = this.themeVariablesMap[iliadVariableName].replace('{THEME_NAME}', cleanedTheme)
      const value = this.getCssVariableValue(cssVariableName)

      if (value !== '') {
        this.setCssVariable(iliadVariableName, value)
      }
    })
  }

  /**
   * returns value from a given css variable
   * @param variableName
   */
  public getCssVariableValue (variableName: string, asNumber: boolean = false): string | number {
    console.assert(!!variableName, 'variableName must be defined')
    const value = getComputedStyle(document.documentElement).getPropertyValue(variableName)

    // return only number without unit
    if (asNumber) {
      return parseFloat(value.replace(/[^0-9.]/g, ''))
    }

    return value
  }

  /**
   * upsert css variables from a given object
   * @param cssVariables
   */
  public setCssVariables (cssVariables: { [key: string]: string }): void {
    const variableNameList = Object.keys(cssVariables) ?? []

    variableNameList
    .filter(variableName => {
      const hasValidName = variableName.length > 0
      const hasValidValue = cssVariables[variableName].length > 0

      return hasValidName && hasValidValue
    })
    .forEach(variableName => {
      this.setCssVariable(variableName, cssVariables[variableName])
    })
  }

  /**
   * upsert a css variable
   * @param variableName
   * @param variableValue
   */
  public setCssVariable (variableName: string, variableValue: string | number): void {
    console.assert(
      !!variableName && !!variableValue,
      'variableName and variableValue must be defined'
    )

    if (typeof variableValue === 'number') {
      variableValue = variableValue.toString()
    }

    document.documentElement.style.setProperty(variableName, variableValue)
  }

  /**
   * preloads images from a given list of urls
   * useful in cases where we know we'll need to render hefty images later on  a given flow
   * @param imageSrcs
   */
  public preloadImages (imageUrls: string[]): Promise<any[]> {
    const promises = imageUrls.map((url) => {
      return new Promise((resolve, reject) => {
        const image = new Image()

        // adding this ignore because firefox doesn't support fetchPriority hints
        // we can safely remove this whenever FF is ready for it
        // @ts-ignore
        image.fetchPriority = 'low'
        image.src = url

        image.onload = () => resolve(image)
        image.onerror = () => reject(`Image failed to load: ${url}`)
      })
    })

    return Promise.all(promises)
  }
}
